import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Shared auth status cache
let authStatusCache = {
  status: null,
  timestamp: null,
  promise: null
};

const CACHE_DURATION = 60000; // 1 minute cache

export function useAuthStatus() {
  const { user, isLoading: authLoading } = useAuth();
  const [authStatus, setAuthStatus] = useState(authStatusCache.status);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const checkAuthStatus = async (forceRefresh = false) => {
    const now = Date.now();

    // Check if we have a valid cached result
    if (!forceRefresh &&
        authStatusCache.status !== null &&
        authStatusCache.timestamp &&
        (now - authStatusCache.timestamp) < CACHE_DURATION) {
      return authStatusCache.status;
    }

    // Check if there's already a request in progress
    if (!forceRefresh && authStatusCache.promise) {
      try {
        const result = await authStatusCache.promise;
        if (isMounted.current) {
          setAuthStatus(result);
        }
        return result;
      } catch (err) {
        if (isMounted.current) {
          setError(err.message);
          setAuthStatus(false);
        }
        return false;
      }
    }

    // Create new request
    setLoading(true);
    setError(null);

    const promise = api.testAuth()
      .then((response) => {
        const isAuthenticated = response.message && response.message.includes("Authenticated successfully");
        authStatusCache = {
          status: isAuthenticated,
          timestamp: Date.now(),
          promise: null
        };
        if (isMounted.current) {
          setAuthStatus(isAuthenticated);
          setLoading(false);
        }
        return isAuthenticated;
      })
      .catch((err) => {
        authStatusCache = {
          status: false,
          timestamp: Date.now(),
          promise: null
        };
        if (isMounted.current) {
          setError(err.message);
          setAuthStatus(false);
          setLoading(false);
        }
        return false;
      });

    // Store the promise to prevent duplicate requests
    authStatusCache.promise = promise;

    return promise;
  };

  useEffect(() => {
    // Only check auth status if we have a user and auth is loaded
    if (user && !authLoading) {
      checkAuthStatus();
    } else if (!user && !authLoading) {
      // Clear cache and status when user logs out
      authStatusCache = {
        status: null,
        timestamp: null,
        promise: null
      };
      setAuthStatus(null);
      setError(null);
    }
  }, [user, authLoading]);

  return {
    authStatus,
    loading: loading || authLoading,
    error,
    checkAuthStatus
  };
}